import { format } from "date-fns";
import { X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TimeSlot } from "@/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface TimeSlotGridProps {
  timeSlots: TimeSlot[];
}

export const TimeSlotGrid = ({ timeSlots }: TimeSlotGridProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleRemoveTimeSlot = async (id: string) => {
    try {
      const { error } = await supabase
        .from('time_slots_created')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error removing time slot:', error);
        toast({
          title: "Error",
          description: "Failed to remove time slot",
          variant: "destructive",
        });
        return;
      }

      queryClient.invalidateQueries({ queryKey: ['timeSlotsCreated'] });
      queryClient.invalidateQueries({ queryKey: ['timeSlotsToSelect'] });
      toast({
        title: "Success",
        description: "Time slot removed successfully",
      });
    } catch (error) {
      console.error('Error removing time slot:', error);
      toast({
        title: "Error",
        description: "Failed to remove time slot",
        variant: "destructive",
      });
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const period = Number(hours) >= 12 ? 'PM' : 'AM';
    const hour12 = Number(hours) % 12 || 12;
    return `${hour12}:${minutes} ${period}`;
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <h2 className="text-xl font-semibold mb-6">Created Time Slots</h2>
      
      <div className="space-y-3">
        {timeSlots
          .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            if (dateA.getTime() !== dateB.getTime()) {
              return dateA.getTime() - dateB.getTime();
            }
            return a.start_time.localeCompare(b.start_time);
          })
          .map((slot) => (
            <div
              key={slot.id}
              className="p-3 bg-gray-50 rounded-lg border border-gray-100 relative"
            >
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Time Slot</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2">
                      <p>Are you sure you want to delete this time slot?</p>
                      <p>
                        {format(new Date(slot.date), "EEEE, MMMM d, yyyy")}
                        {" at "}
                        {formatTime(slot.start_time)}
                      </p>
                      {slot.booked_players > 0 && (
                        <p className="text-red-500 font-medium">
                          Warning: This slot has {slot.booked_players} player{slot.booked_players !== 1 ? 's' : ''} booked 
                          {slot.paid_players > 0 && ` (${slot.paid_players} paid)`}.
                        </p>
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleRemoveTimeSlot(slot.id)}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              
              <h3 className="text-sm font-medium mb-1.5">
                {format(new Date(slot.date), "EEEE, MMMM d, yyyy")}
                {" at "}
                {formatTime(slot.start_time)}
              </h3>
              
              <p className="text-xs text-gray-600 mb-1.5">
                Max. Players: {slot.max_players} | Max. Guests: {slot.max_guests_per_player} | Fees: {slot.fee} AED
              </p>
              
              <div className="text-xs space-y-0.5">
                <p>
                  <span className="text-blue-600">
                    Booked: {slot.booked_players + slot.reserved_slots}/{slot.max_players}
                  </span>
                  {" • "}
                  <span className="text-green-600">
                    Paid: {slot.paid_players + slot.reserved_paid_slots}/{slot.max_players}
                  </span>
                </p>
                <p className="text-gray-500">
                  Reserved: {slot.reserved_slots} ({slot.reserved_paid_slots} paid)
                </p>
              </div>
            </div>
          ))}
        
        {timeSlots.length === 0 && (
          <p className="text-gray-500 text-center py-4">
            No time slots created yet
          </p>
        )}
      </div>
    </div>
  );
};
